// The subcontractor's RATE CARD on their page (Session AUU): every version with its effective date, the schedule of rates
// the version in force today gives each linked engineer, and the editor to add a version or change one in place.
//
// ADMIN ONLY. The page renders this only for an admin (the pay check figure's own access pattern; the server refuses the
// read to anyone else). Rates are what ELE pays; they never reach a customer or a subcontractor from here.
//
// EVERY SAVE SHOWS THE DIFFERENCE FIRST. The editor's "Review changes" asks the server for a preview: the line-by-line
// difference against the version it supersedes (or the version being edited) and every ALREADY APPROVED week the change
// would reprice. Only then may the office save; a save that would move an approved week needs the explicit confirmation
// box ticked, which the server also requires (affectsApprovedWeeks). A delete is the destructive two-step modal and is
// refused by the server while the version prices an approved week.
//
// Money is typed in pounds and sent as whole pence; a blank line is "not agreed" and the pay engine names those hours
// rather than pricing them. Dates through lib/format; no hex, no local formatter (the appContrast ratchet).
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, api } from "../../lib/api";
import type { RateCardApprovedWeek, RateCardChange, RateCardLineKey, RateCardVersion, RateCardWriteBody, SubcontractorRateCardView } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { ModalShell } from "../../components/ModalShell";
import { formatDateLong } from "../../lib/format";
import { StatusPill } from "../../components/StatusPill";
import { RATE_LINES as RC1_RATE_LINES, RC1_SETTING_DEFAULTS, clockText, clockToMins, londonToday } from "../../lib/rateCardUi";
import { RateCardTable, UpliftSelect } from "./RateCardFigures";

// Session RC1: every line of the card, the technician visit block and "Modernisation or installation" included, in the
// order the office reads them (one list, lib/rateCardUi.ts).
export const RATE_LINES: { key: RateCardLineKey; label: string; unit: string }[] = RC1_RATE_LINES;

/** "60.00" -> 6000; "" -> null; anything else -> undefined (invalid). Exported for the test. */
export function poundsToPence(text: string): number | null | undefined {
  const t = text.trim();
  if (t === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return undefined;
  const [whole, frac = ""] = t.split(".");
  return Number(whole) * 100 + Number((frac + "00").slice(0, 2));
}

export function penceToPounds(pence: number | null | undefined): string {
  if (pence == null) return "";
  return `${Math.floor(pence / 100)}.${String(pence % 100).padStart(2, "0")}`;
}

type FigureText = Record<RateCardLineKey, string>;
interface FormState {
  effectiveFrom: string;
  figures: FigureText;
  dailyCapThresholdMins: string;
  saturdayMultiplierPct: string;
  sundayMultiplierPct: string;
  travelCapPerLegMins?: string;
  // Session RC1: the uplift bands (whole percentages, shown in words) and the technician block's hours. Optional so an
  // older form shape still builds; absent means the defaults.
  weekdayEveningMultiplierPct?: string;
  weekdayEveningStart?: string;
  bankHolidayMultiplierPct?: string;
  techVisitBlockHours?: string;
  sourceNote: string;
  // Session AUZ: the Labour Only deduction as a percentage in text ("20", "12.5"); blank = NOT SET. Never defaulted.
  labourOnlyDeductionPct: string;
  engineerRates: Record<string, FigureText>;
}

/** Basis points to the percentage text the form shows; null (not set) is blank. */
export function bpsToPercentText(bps: number | null | undefined): string {
  if (bps == null) return "";
  return `${(bps / 100).toFixed(2).replace(/\.?0+$/, "")}`;
}

/** The percentage text to basis points: null for blank (not set), undefined for text that is not a percentage. */
export function percentTextToBps(text: string): number | null | undefined {
  const t = text.trim().replace(/%$/, "").trim();
  if (t === "") return null;
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(t)) return undefined;
  const n = Math.round(Number(t) * 100);
  return n > 10_000 ? undefined : n;
}

function emptyFigures(): FigureText {
  return Object.fromEntries(RATE_LINES.map((l) => [l.key, ""])) as FigureText;
}

/**
 * The engineers on a version's own rows who are NOT linked to this subcontractor (AUU fix-up): the migrated version 1 per-engineer
 * lines exist before the office records the link. Their lines are CARRIED through an edit untouched (the server allows a
 * carry-forward, never a new line, for an unlinked engineer) and named here so the office knows they are waiting.
 */
export function carriedEngineerRows(v: RateCardVersion | null, engineers: { id: string }[]): { engineerId: string; engineerName: string }[] {
  const linked = new Set(engineers.map((e) => e.id));
  return (v?.engineerRates ?? []).filter((r) => !linked.has(r.engineerId)).map((r) => ({ engineerId: r.engineerId, engineerName: r.engineerName ?? "An engineer not linked to this subcontractor" }));
}

function formFromVersion(v: RateCardVersion | null, engineers: { id: string }[], todayYmd: string): FormState {
  const figures = emptyFigures();
  if (v) for (const l of RATE_LINES) figures[l.key] = penceToPounds(v[l.key]);
  const engineerRates: Record<string, FigureText> = {};
  for (const e of [...engineers, ...carriedEngineerRows(v, engineers).map((r) => ({ id: r.engineerId }))]) {
    const own = v?.engineerRates.find((r) => r.engineerId === e.id);
    const f = emptyFigures();
    if (own) for (const l of RATE_LINES) f[l.key] = penceToPounds(own[l.key]);
    engineerRates[e.id] = f;
  }
  return {
    effectiveFrom: v ? v.effectiveFrom : todayYmd,
    figures,
    dailyCapThresholdMins: String(v?.dailyCapThresholdMins ?? 480),
    saturdayMultiplierPct: String(v?.saturdayMultiplierPct ?? 100),
    sundayMultiplierPct: String(v?.sundayMultiplierPct ?? 100),
    sourceNote: v?.sourceNote ?? "",
    labourOnlyDeductionPct: bpsToPercentText(v?.labourOnlyDeductionBps),
    travelCapPerLegMins: v?.travelCapPerLegMins == null ? "" : String(v.travelCapPerLegMins),
    weekdayEveningMultiplierPct: String(v?.weekdayEveningMultiplierPct ?? RC1_SETTING_DEFAULTS.weekdayEveningMultiplierPct),
    weekdayEveningStart: clockText(v?.weekdayEveningStartMins ?? RC1_SETTING_DEFAULTS.weekdayEveningStartMins),
    bankHolidayMultiplierPct: String(v?.bankHolidayMultiplierPct ?? RC1_SETTING_DEFAULTS.bankHolidayMultiplierPct),
    techVisitBlockHours: String((v?.techVisitBlockMins ?? RC1_SETTING_DEFAULTS.techVisitBlockMins) / 60),
    engineerRates,
  };
}

/** Pure: the request body from the form, or the first problem in words. Exported for the test. */
export function bodyFromForm(form: FormState, engineers: { id: string; name: string }[]): { body: RateCardWriteBody } | { error: string } {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.effectiveFrom)) return { error: "Enter the effective date." };
  const body: RateCardWriteBody = { effectiveFrom: form.effectiveFrom, engineerRates: [] };
  for (const l of RATE_LINES) {
    const p = poundsToPence(form.figures[l.key]);
    if (p === undefined) return { error: `${l.label}: enter pounds and pence, for example 60.00, or leave it blank for not agreed.` };
    body[l.key] = p;
  }
  const threshold = Number(form.dailyCapThresholdMins);
  if (!Number.isInteger(threshold) || threshold < 1 || threshold > 1440) return { error: "The daily cap threshold is a number of minutes (480 is 8 hours)." };
  body.dailyCapThresholdMins = threshold;
  for (const [k, label] of [["saturdayMultiplierPct", "Saturday"], ["sundayMultiplierPct", "Sunday"]] as const) {
    const n = Number(form[k]);
    if (!Number.isInteger(n) || n < 100 || n > 1000) return { error: `The ${label} multiplier is not one of the uplifts: choose Normal rate, Time and a half (x1.5) or Double time (x2).` };
    body[k] = n;
  }
  // Session RC1: the evening and bank holiday uplifts, the evening's start and the block's hours, carried on every save.
  for (const [k, label] of [["weekdayEveningMultiplierPct", "weekday evening"], ["bankHolidayMultiplierPct", "bank holiday"]] as const) {
    const n = Number(form[k] ?? RC1_SETTING_DEFAULTS[k]);
    if (!Number.isInteger(n) || n < 100 || n > 1000) return { error: `Choose the ${label} uplift.` };
    body[k] = n;
  }
  const start = form.weekdayEveningStart == null ? RC1_SETTING_DEFAULTS.weekdayEveningStartMins : clockToMins(form.weekdayEveningStart);
  if (start == null) return { error: "Enter the time the weekday evening uplift starts, for example 18:00." };
  body.weekdayEveningStartMins = start;
  const blockMins = form.techVisitBlockHours == null ? RC1_SETTING_DEFAULTS.techVisitBlockMins : Math.round(Number(form.techVisitBlockHours) * 60);
  if (!Number.isFinite(blockMins) || blockMins < 15 || blockMins > 1440 || blockMins % 15 !== 0) return { error: "The technician block covers a number of hours on site, in quarter hours (5 is five hours)." };
  body.techVisitBlockMins = blockMins;
  body.sourceNote = form.sourceNote.trim() === "" ? null : form.sourceNote.trim();
  // The Labour Only deduction (Session AUZ): carried on EVERY save, blank meaning not set, because the route reads a
  // missing field as null and an edit from this form used to wipe a deduction set elsewhere.
  const bps = percentTextToBps(form.labourOnlyDeductionPct);
  if (bps === undefined) return { error: "The Labour Only deduction is a percentage off the Bona Fide figures, for example 20 or 12.5, or blank for not set." };
  body.labourOnlyDeductionBps = bps;
  // The travel cap per leg (Session BAL): carried on EVERY save, blank meaning no cap, for the same reason as the deduction.
  const capText = (form.travelCapPerLegMins ?? "").trim();
  if (capText === "") body.travelCapPerLegMins = null;
  else {
    const cap = Number(capText);
    if (!Number.isInteger(cap) || cap < 15 || cap > 600) return { error: "The travel cap is the most minutes one travel leg is paid for, a whole number from 15 to 600 (60 is one hour), or blank for no cap." };
    body.travelCapPerLegMins = cap;
  }
  // Every engineer the form holds: the linked ones, and any carried through from the version's own rows (an engineer
  // not yet linked keeps their lines; they are not editable here because they are not linked, only kept).
  for (const engineerId of Object.keys(form.engineerRates)) {
    const f = form.engineerRates[engineerId];
    if (!f) continue;
    const name = engineers.find((e) => e.id === engineerId)?.name ?? "An engineer not linked to this subcontractor";
    const own: { engineerId: string } & Partial<Record<RateCardLineKey, number | null>> = { engineerId };
    let any = false;
    for (const l of RATE_LINES) {
      const p = poundsToPence(f[l.key]);
      if (p === undefined) return { error: `${name}, ${l.label}: enter pounds and pence, or leave it blank to use the card's rate.` };
      if (p != null) {
        own[l.key] = p;
        any = true;
      }
    }
    if (any) body.engineerRates!.push(own);
  }
  return { body };
}

const INPUT = "w-full rounded-ele border border-ele-field-border bg-ele-field px-2 py-1 text-sm text-ele-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ele-green";

export function SubcontractorRateCardSection({ subId }: { subId: string }) {
  const [view, setView] = useState<SubcontractorRateCardView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ version: RateCardVersion | null } | null>(null);
  const [deleting, setDeleting] = useState<RateCardVersion | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.subcontractors.rateCards.get(subId).then(
      (v) => {
        setView(v);
        setLoadError(null);
      },
      (err) => setLoadError(friendlyApiError(err, "Could not load the rate card.")),
    );
  }, [subId]);

  useEffect(() => {
    load();
  }, [load]);

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.subcontractors.rateCards.remove(subId, deleting.id);
      setNotice(`Version ${deleting.version} (from ${formatDateLong(deleting.effectiveFrom)}) deleted.`);
      setDeleting(null);
      load();
    } catch (err) {
      setDeleteError(friendlyApiError(err, "Could not delete that version."));
    } finally {
      setDeleteBusy(false);
    }
  }

  const current = view?.versions.find((v) => v.id === view.currentVersionId) ?? null;

  return (
    <section className="mb-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-4" aria-label="Rate card">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="flex flex-wrap items-center gap-2 font-heading text-lg uppercase tracking-wide text-ele-blue">
            Rate card
            {current?.standardVersion != null ? (
              <StatusPill word={`Standard v${current.standardVersion}`} tone="purple" />
            ) : view?.rateCardSource === "CUSTOM" || (current && current.standardVersion == null) ? (
              <StatusPill word="Custom" tone="grey" />
            ) : null}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-ele-body">
            What this subcontractor is paid, per line, by version and effective date. The pay check figure prices each day of a linked engineer&apos;s week
            from the version in force on that day; a line left blank is not agreed and those hours are named as unpriced rather than guessed. Internal only:
            nothing here reaches a customer or the subcontractor.
          </p>
        </div>
        {view && (
          <Button variant="primary" size="sm" onClick={() => setEditing({ version: null })}>
            Add a version
          </Button>
        )}
      </div>
      {loadError && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">
          {loadError}
        </p>
      )}
      {notice && <p className="mb-3 rounded-ele border border-ele-green px-3 py-2 text-sm text-ele-strong">{notice}</p>}

      {!view && !loadError ? (
        <Loader label="Loading the rate card..." rows={3} />
      ) : view && view.versions.length === 0 ? (
        <EmptyState
          compact
          message="No rate card yet."
          hint={
            view.engineers.length === 0
              ? "No engineer is linked to this subcontractor yet, so nothing is priced from it. Link the engineer first (Engineers page, edit, Subcontractor record), then add a version."
              : "Every linked engineer's week is unpriced until a version is added."
          }
        />
      ) : view ? (
        <>
          {view.engineers.length === 0 && (
            <p role="status" className="mb-3 rounded-ele border border-ele-purple-accent px-3 py-2 text-sm text-ele-body">
              No engineer is linked to this subcontractor, so this card prices nobody yet: each of their weeks reads &ldquo;not linked to a subcontractor&rdquo;
              until the link is recorded. Link each engineer on the Engineers page (edit, Subcontractor record), or ask Guvnor to link_engineer_subcontractor.
              Linking an engineer who already has approved weeks prices those weeks from this card (unpriced to priced), so the Engineers page shows them
              and asks you to confirm before it saves.
              {carriedEngineerRows(current ?? view.versions[view.versions.length - 1] ?? null, view.engineers).length > 0 &&
                ` Own rates are already held for ${carriedEngineerRows(current ?? view.versions[view.versions.length - 1] ?? null, view.engineers)
                  .map((r) => r.engineerName.replace(/ \(not linked to this subcontractor\)$/, ""))
                  .join(" and ")}, and apply the moment the link is recorded.`}
            </p>
          )}
          {view.schedule && current ? (
            <ScheduleTable view={view} current={current} />
          ) : (
            <p role="status" className="mb-3 text-sm text-ele-body">
              No version is in force today: the earliest version starts {formatDateLong(view.versions[0]!.effectiveFrom)}.
            </p>
          )}

          <p className="mt-4 font-menu text-xs uppercase tracking-widest text-ele-blue">Versions</p>
          <ul className="mt-1 divide-y divide-ele-purple-accent/40">
            {[...view.versions].reverse().map((v) => (
              <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <div>
                  <span className="text-ele-strong">Version {v.version}</span>
                  {v.standardVersion != null && <span className="text-ele-body"> (Standard v{v.standardVersion})</span>}
                  <span className="text-ele-body"> from {formatDateLong(v.effectiveFrom)}</span>
                  {v.id === view.currentVersionId && <span className="ml-2 font-menu text-xs uppercase tracking-widest text-ele-green">In force</span>}
                  <span className="block text-xs text-ele-body">
                    {v.createdByName ? `Saved by ${v.createdByName}, ` : ""}
                    {formatDateLong(v.updatedAt)}
                    {v.sourceNote ? `. ${v.sourceNote}` : ""}
                  </span>
                  <span className="block text-xs text-ele-body">
                    Labour Only deduction: {v.labourOnlyDeductionBps == null ? "not set (Labour Only rate not set)" : `${bpsToPercentText(v.labourOnlyDeductionBps)}% off the Bona Fide figures`}
                  </span>
                  <span className="block text-xs text-ele-body">Travel cap per leg: {v.travelCapPerLegMins == null ? "none" : `${v.travelCapPerLegMins} minutes`}</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setEditing({ version: v })}>
                    Edit version {v.version}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleting(v)}>
                    Delete
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {editing && view && (
        <RateCardEditor
          subId={subId}
          view={view}
          version={editing.version}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null);
            setNotice(message);
            load();
          }}
        />
      )}

      <ConfirmDeleteModal
        open={deleting != null}
        title={deleting ? `Delete version ${deleting.version}?` : "Delete"}
        message={deleting ? `Version ${deleting.version}, in force from ${formatDateLong(deleting.effectiveFrom)}, will be removed permanently. It cannot be deleted while it prices an already approved week.` : ""}
        confirmLabel="Delete this version"
        variant="destructive"
        busy={deleteBusy}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          setDeleting(null);
          setDeleteError(null);
        }}
      />
    </section>
  );
}

function ScheduleTable({ view, current }: { view: SubcontractorRateCardView; current: RateCardVersion }) {
  const schedule = view.schedule!;
  return (
    <div>
      <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">
        In force today: version {current.version}, from {formatDateLong(current.effectiveFrom)}
      </p>
      {/* Session RC1: Bona Fide and Labour Only side by side, every line, the uplifts in words. */}
      <RateCardTable card={current} />
      {schedule.ratesDifferByPerson && (
        <>
          <p className="mt-4 font-menu text-xs uppercase tracking-widest text-ele-blue">Own rates by engineer (Bona Fide)</p>
          <table className="ele-list-table mt-2 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ele-body">
                <th className="py-1 pr-2">Line</th>
                <th className="py-1 pr-2">Card</th>
                {view.engineers.map((e) => (
                  <th key={e.id} className="py-1 pr-2">
                    {e.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {schedule.lines
                .filter((l) => l.perEngineer.some((p) => p.ownRate))
                .map((l) => (
                  <tr key={l.key} className="border-t border-ele-purple-accent/40">
                    <td className="py-1 pr-2 text-ele-strong">
                      {l.label} <span className="text-xs text-ele-body">{l.unit}</span>
                    </td>
                    <td className="py-1 pr-2 text-ele-body">{l.card}</td>
                    {l.perEngineer.map((p) => (
                      <td key={p.engineerName} className={`py-1 pr-2 ${p.ownRate ? "text-ele-strong" : "text-ele-body"}`}>
                        {p.rate}
                        {p.ownRate && <span className="ml-1 text-xs text-ele-body">(own rate)</span>}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      )}
      <ul className="mt-2 space-y-1 text-xs text-ele-body">
        <li>{schedule.rules.rounding}</li>
        <li>{schedule.rules.twoMan}</li>
      </ul>
    </div>
  );
}

function RateCardEditor({
  subId,
  view,
  version,
  onClose,
  onSaved,
}: {
  subId: string;
  view: SubcontractorRateCardView;
  version: RateCardVersion | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const todayYmd = useMemo(() => londonToday(), []);
  // A new version starts from the version in force today (the usual case is "the same card, a few figures changed").
  const base = version ?? view.versions.find((v) => v.id === view.currentVersionId) ?? view.versions[view.versions.length - 1] ?? null;
  const [form, setForm] = useState<FormState>(() => {
    const f = formFromVersion(base, view.engineers, todayYmd);
    return version ? f : { ...f, effectiveFrom: todayYmd };
  });
  const [showOwn, setShowOwn] = useState<Record<string, boolean>>(() => Object.fromEntries(view.engineers.map((e) => [e.id, (base?.engineerRates ?? []).some((r) => r.engineerId === e.id)])));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ changes: RateCardChange[]; approvedWeeksMoved: RateCardApprovedWeek[]; version: number; movesToCustomMessage: string | null } | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const setFigure = (key: RateCardLineKey, value: string) => {
    setPreview(null);
    setForm((f) => ({ ...f, figures: { ...f.figures, [key]: value } }));
  };
  const setOwn = (engineerId: string, key: RateCardLineKey, value: string) => {
    setPreview(null);
    setForm((f) => ({ ...f, engineerRates: { ...f.engineerRates, [engineerId]: { ...f.engineerRates[engineerId]!, [key]: value } } }));
  };
  const setField = <
    K extends
      | "effectiveFrom"
      | "dailyCapThresholdMins"
      | "saturdayMultiplierPct"
      | "sundayMultiplierPct"
      | "sourceNote"
      | "labourOnlyDeductionPct"
      | "travelCapPerLegMins"
      | "weekdayEveningMultiplierPct"
      | "weekdayEveningStart"
      | "bankHolidayMultiplierPct"
      | "techVisitBlockHours",
  >(
    k: K,
    value: string,
  ) => {
    setPreview(null);
    setForm((f) => ({ ...f, [k]: value }));
  };

  async function review() {
    const built = bodyFromForm(form, view.engineers);
    if ("error" in built) {
      setError(built.error);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = version
        ? await api.subcontractors.rateCards.update(subId, version.id, { ...built.body, preview: true })
        : await api.subcontractors.rateCards.create(subId, { ...built.body, preview: true });
      if ("preview" in res && res.preview) {
        setPreview({ changes: res.changes, approvedWeeksMoved: res.approvedWeeksMoved, version: res.version, movesToCustomMessage: res.movesToCustom ? (res.movesToCustomMessage ?? null) : null });
        setAcknowledged(false);
      }
    } catch (err) {
      setError(friendlyApiError(err, "Could not review the change."));
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    const built = bodyFromForm(form, view.engineers);
    if ("error" in built || !preview) return;
    setBusy(true);
    setError(null);
    try {
      const body: RateCardWriteBody = { ...built.body, ...(preview.approvedWeeksMoved.length > 0 && acknowledged ? { affectsApprovedWeeks: true } : {}) };
      const res = version ? await api.subcontractors.rateCards.update(subId, version.id, body) : await api.subcontractors.rateCards.create(subId, body);
      if ("version" in res && typeof res.version === "object") {
        const v = res.version;
        onSaved(version ? `Version ${v.version} saved. ${res.changes.length} change${res.changes.length === 1 ? "" : "s"} recorded.` : `Version ${v.version} added, in force from ${formatDateLong(v.effectiveFrom)}.`);
      } else {
        onSaved("Saved.");
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.body?.error === "approved_weeks_affected") {
        setError(err.message);
        setPreview(null);
      } else {
        setError(friendlyApiError(err, "Could not save the rate card."));
      }
    } finally {
      setBusy(false);
    }
  }

  const needsAck = (preview?.approvedWeeksMoved.length ?? 0) > 0;
  const canSave = preview != null && preview.changes.length > 0 && (!needsAck || acknowledged);

  return (
    <ModalShell ariaLabel={version ? `Edit version ${version.version}` : "Add a rate card version"} onClose={onClose} width="max-w-[720px]">
      <h2 className="mb-3 font-heading text-2xl tracking-wide text-ele-strong">{version ? `Edit version ${version.version}` : "Add a version"}</h2>
      <p className="mb-3 text-sm text-ele-body">
        Pounds and pence per line, for example 60.00. Leave a line blank for not agreed. Review the changes before saving: the review names every already approved
        week the change would reprice.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-ele-body">
          Effective from
          <input type="date" className={INPUT} value={form.effectiveFrom} onChange={(e) => setField("effectiveFrom", e.target.value)} />
        </label>
        <label className="text-sm text-ele-body">
          Source note
          <input type="text" className={INPUT} value={form.sourceNote} maxLength={500} placeholder="Where the figures came from" onChange={(e) => setField("sourceNote", e.target.value)} />
        </label>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {RATE_LINES.map((l) => (
          <label key={l.key} className="text-sm text-ele-body">
            {l.label} <span className="text-xs">({l.unit})</span>
            <input type="text" inputMode="decimal" className={INPUT} value={form.figures[l.key]} placeholder="Not agreed" onChange={(e) => setFigure(l.key, e.target.value)} />
          </label>
        ))}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <label className="text-sm text-ele-body">
          Daily cap threshold (minutes)
          <input type="text" inputMode="numeric" className={INPUT} value={form.dailyCapThresholdMins} onChange={(e) => setField("dailyCapThresholdMins", e.target.value)} />
        </label>
        <label className="text-sm text-ele-body">
          Technician block covers (hours on site)
          <input type="text" inputMode="decimal" className={INPUT} value={form.techVisitBlockHours ?? "5"} onChange={(e) => setField("techVisitBlockHours", e.target.value)} />
        </label>
      </div>
      {/* Session RC1: the uplifts on call-out and repair labour, chosen in words, never typed as a percentage. */}
      <p className="mt-4 font-menu text-xs uppercase tracking-widest text-ele-blue">Uplifts on call-out and repair labour</p>
      <div className="mt-1 grid gap-3 sm:grid-cols-3">
        <label className="text-sm text-ele-body">
          Weekday evening starts at
          <input type="time" className={INPUT} value={form.weekdayEveningStart ?? "18:00"} onChange={(e) => setField("weekdayEveningStart", e.target.value)} />
        </label>
        <UpliftSelect label="Weekdays from then" value={form.weekdayEveningMultiplierPct ?? "100"} onChange={(v) => setField("weekdayEveningMultiplierPct", v)} />
        <UpliftSelect label="Saturday" value={form.saturdayMultiplierPct} onChange={(v) => setField("saturdayMultiplierPct", v)} />
        <UpliftSelect label="Sunday" value={form.sundayMultiplierPct} onChange={(v) => setField("sundayMultiplierPct", v)} />
        <UpliftSelect label="Bank holidays" value={form.bankHolidayMultiplierPct ?? "100"} onChange={(v) => setField("bankHolidayMultiplierPct", v)} />
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <label className="text-sm text-ele-body">
          Labour Only deduction (percent off every Bona Fide figure)
          <input
            type="text"
            inputMode="decimal"
            className={INPUT}
            value={form.labourOnlyDeductionPct}
            placeholder="Not set"
            aria-describedby="labour-only-deduction-help"
            onChange={(e) => setField("labourOnlyDeductionPct", e.target.value)}
          />
        </label>
        <p id="labour-only-deduction-help" className="text-xs text-ele-body sm:col-span-2">
          Applied by the timesheet check figure while the subcontractor&rsquo;s status in force is Labour Only, and only once they have signed the agreement with
          clause 12.5 (the two-column Schedule 1). Blank is not set: every figure stays the Bona Fide figure and the pay panel says &ldquo;Labour Only rate not
          set&rdquo;. It is never invented.
        </p>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <label className="text-sm text-ele-body">
          Cap on one paid travel leg (minutes)
          <input
            type="text"
            inputMode="numeric"
            className={INPUT}
            value={form.travelCapPerLegMins ?? ""}
            placeholder="No cap"
            aria-describedby="travel-cap-help"
            onChange={(e) => setField("travelCapPerLegMins", e.target.value)}
          />
        </label>
        <p id="travel-cap-help" className="text-xs text-ele-body sm:col-span-2">
          The most one paid travel leg is paid for, after the 15 minute rounding. 60 on a GBP 30 an hour travel rate pays at most GBP 30 a leg. Blank is no cap. The recorded
          minutes still count toward the day&rsquo;s threshold in full.
        </p>
      </div>

      {carriedEngineerRows(base, view.engineers).length > 0 && (
        <p role="status" className="mt-4 rounded-ele border border-ele-purple-accent px-3 py-2 text-sm text-ele-body">
          Own rates kept for {carriedEngineerRows(base, view.engineers).map((r) => r.engineerName).join("; ")}. They are carried through this save unchanged and
          apply once the engineer is linked to this subcontractor (Engineers page, edit, Subcontractor record; a link that would price already approved
          weeks is shown there and confirmed before it saves).
        </p>
      )}
      {view.engineers.length > 0 && (
        <div className="mt-4">
          <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">Own rates, where a line differs by person</p>
          {view.engineers.map((e) => (
            <div key={e.id} className="mt-2 rounded-ele border border-ele-purple-accent/60 p-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-ele-strong">
                  {e.name}
                  {!e.active && <span className="text-xs text-ele-body"> (deactivated)</span>}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setShowOwn((s) => ({ ...s, [e.id]: !s[e.id] }))}>
                  {showOwn[e.id] ? `Hide ${e.name}'s own rates` : `Set ${e.name}'s own rates`}
                </Button>
              </div>
              {showOwn[e.id] && (
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  {RATE_LINES.map((l) => (
                    <label key={l.key} className="text-xs text-ele-body">
                      {l.label}
                      <input type="text" inputMode="decimal" className={INPUT} value={form.engineerRates[e.id]?.[l.key] ?? ""} placeholder="The card's rate" onChange={(ev) => setOwn(e.id, l.key, ev.target.value)} />
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}

      {preview && (
        <div role="status" className="mt-3 rounded-ele border border-ele-purple-accent px-3 py-2 text-sm">
          {/* Session RC1: the office's own figures take a Standard-card subcontractor onto custom rates. Said before the save. */}
          {preview.movesToCustomMessage && (
            <p className="mb-2 rounded-ele border border-ele-amber-line px-3 py-2 text-ele-strong" data-testid="moves-to-custom">
              {preview.movesToCustomMessage}
            </p>
          )}
          <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">{version ? `Changes to version ${preview.version}` : `Version ${preview.version}, the difference from the version it supersedes`}</p>
          {preview.changes.length === 0 ? (
            <p className="mt-1 text-ele-body">Nothing changes.</p>
          ) : (
            <ul className="mt-1 space-y-1">
              {preview.changes.map((c, i) => (
                <li key={i} className="text-ele-body">
                  <span className="text-ele-strong">
                    {c.what}
                    {c.engineerName ? ` (${c.engineerName})` : ""}
                  </span>
                  : {c.before} to {c.after}
                </li>
              ))}
            </ul>
          )}
          {needsAck && (
            <div className="mt-2">
              <p className="text-ele-strong">
                This change would reprice {preview.approvedWeeksMoved.length} already approved week{preview.approvedWeeksMoved.length === 1 ? "" : "s"}:{" "}
                {preview.approvedWeeksMoved.map((w) => `${w.engineerName}, week beginning ${formatDateLong(w.weekStarting)}`).join("; ")}.
              </p>
              <label className="mt-1 flex items-center gap-2 text-sm text-ele-body">
                <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
                Yes, reprice those approved weeks. The change is recorded against my name.
              </label>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="secondary" onClick={() => void review()} disabled={busy}>
          Review changes
        </Button>
        <Button variant="primary" onClick={() => void save()} disabled={busy || !canSave}>
          {version ? "Save version" : "Add version"}
        </Button>
      </div>
    </ModalShell>
  );
}
